import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Send, Loader2, FileText, Car } from "lucide-react";

interface SmsComposeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  customerName: string;
  customerPhone: string | null;
  onSent?: () => void;
}

interface Template {
  id: string;
  name: string;
  category: string;
  body: string;
}

interface Vehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  price: number | null;
}

export function SmsComposeDialog({
  open, onOpenChange, customerId, customerName, customerPhone, onSent,
}: SmsComposeDialogProps) {
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [charCount, setCharCount] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [needsVehicle, setNeedsVehicle] = useState(false);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState("");

  useEffect(() => {
    if (open) {
      loadTemplates();
      setMessage("");
      setSelectedTemplate(null);
      setNeedsVehicle(false);
      setSelectedVehicleId("");
    }
  }, [open]);

  useEffect(() => {
    setCharCount(message.length);
  }, [message]);

  const loadTemplates = async () => {
    const { data } = await supabase
      .from("message_templates")
      .select("id, name, category, body")
      .eq("is_active", true)
      .eq("channel", "sms");
    if (data) setTemplates(data);
  };

  const loadVehicles = async () => {
    const { data } = await supabase
      .from("vehicles")
      .select("id, year, make, model, price")
      .eq("status", "available")
      .order("created_at", { ascending: false });
    setVehicles(data || []);
  };

  const resolveTemplate = async (tmpl: Template, vehicleId?: string) => {
    const firstName = customerName.split(" ")[0];
    let body = tmpl.body.replace(/\{\{first_name\}\}/g, firstName);

    // Resolve {{review_url}}
    if (body.includes("{{review_url}}")) {
      const { data: setting } = await supabase
        .from("crm_settings")
        .select("value")
        .eq("key", "google_review_url")
        .maybeSingle();
      const reviewUrl = setting?.value ? String(setting.value).replace(/"/g, '') : '';
      body = body.replace(/\{\{review_url\}\}/g, reviewUrl);
    }

    // Resolve {{vehicle_info}} and {{vehicle_url}}
    if (vehicleId && (body.includes("{{vehicle_info}}") || body.includes("{{vehicle_url}}"))) {
      const v = vehicles.find(veh => veh.id === vehicleId);
      if (v) {
        const vehicleInfo = `${v.year} ${v.make} ${v.model}`;
        const vehicleUrl = `https://carstreet.ca/vehicle/${v.id}`;
        body = body.replace(/\{\{vehicle_info\}\}/g, vehicleInfo);
        body = body.replace(/\{\{vehicle_url\}\}/g, vehicleUrl);
      }
    }

    return body;
  };

  const handleTemplateSelect = async (templateId: string) => {
    const tmpl = templates.find(t => t.id === templateId);
    if (!tmpl) return;

    setSelectedTemplate(tmpl);

    // Check if this template needs a vehicle selection
    if (tmpl.body.includes("{{vehicle_info}}") || tmpl.body.includes("{{vehicle_url}}")) {
      setNeedsVehicle(true);
      await loadVehicles();
      // Don't set message yet — wait for vehicle selection
      setMessage("");
      setSelectedVehicleId("");
    } else {
      setNeedsVehicle(false);
      setSelectedVehicleId("");
      const body = await resolveTemplate(tmpl);
      setMessage(body);
    }
  };

  const handleVehicleSelect = async (vehicleId: string) => {
    setSelectedVehicleId(vehicleId);
    if (selectedTemplate) {
      const body = await resolveTemplate(selectedTemplate, vehicleId);
      setMessage(body);
    }
  };

  const handleSend = async () => {
    if (!customerPhone) {
      toast.error("Customer has no phone number");
      return;
    }
    if (!message.trim()) {
      toast.error("Message is empty");
      return;
    }

    setIsSending(true);
    try {
      const { data: userData } = await supabase.auth.getUser();

      const { data, error } = await supabase.functions.invoke("send-sms", {
        body: {
          to: customerPhone,
          body: message.trim(),
          customer_id: customerId,
          sent_by: userData?.user?.id || null,
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Failed to send");

      toast.success(`SMS sent to ${customerName}`);
      setMessage("");
      onOpenChange(false);
      onSent?.();
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to send SMS");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Send SMS to {customerName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Phone display */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">To:</span>
            {customerPhone ? (
              <Badge variant="outline">{customerPhone}</Badge>
            ) : (
              <Badge variant="destructive">No phone number</Badge>
            )}
          </div>

          {/* Template selector */}
          {templates.length > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <FileText className="h-3.5 w-3.5" />
                <span>Use template</span>
              </div>
              <Select onValueChange={handleTemplateSelect}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Choose a template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id} className="text-xs">
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Vehicle picker — shown when template has vehicle variables */}
          {needsVehicle && (
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <Car className="h-3.5 w-3.5" />
                Select a vehicle to share
              </Label>
              <Select value={selectedVehicleId} onValueChange={handleVehicleSelect}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Pick a vehicle from inventory..." />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.length === 0 ? (
                    <SelectItem value="none" disabled>No available vehicles</SelectItem>
                  ) : (
                    vehicles.map(v => (
                      <SelectItem key={v.id} value={v.id} className="text-xs">
                        {v.year} {v.make} {v.model} — ${v.price?.toLocaleString() || 'N/A'}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Message body */}
          <div className="space-y-1">
            <Textarea
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              className="resize-none"
              maxLength={1600}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{charCount}/1600 characters</span>
              <span>{Math.ceil(charCount / 160)} SMS segment{Math.ceil(charCount / 160) > 1 ? 's' : ''}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={isSending || !message.trim() || !customerPhone}
            >
              {isSending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Send SMS</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
